import React, { useCallback, useContext } from 'react';
import classNames from 'classnames';
import { Button } from 'ncoded-component-library';
import { FileTypes } from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/data/DataTypes';
import Dropzone from 'components/Dropzone';
import DownloadIcon from 'icons/Download.icon';
import { useTranslation } from 'react-i18next';
import { DropzoneUpload } from 'models/Dropzone';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import {
  SINGLE_FILE_MAX_SIZE_DIRECT_UPLOAD,
  SINGLE_FILE_MAX_SIZE_MB,
} from 'data/global';

import './GetAndUploadTemplate.styles.scss';

type GetAndUploadTemplateProps = {
  className?: string;
  fileName: string;
  fields?: {
    field: string;
    description: string;
  }[];
  supportedFileTypes: FileTypes[];
  isDownoadingTemplate: boolean;
  showFields?: boolean;
  showColumnsTemplate?: boolean;
  downloadTemplate: () => void;
  uploadRequest: DropzoneUpload;
};

const GetAndUploadTemplate: React.FC<GetAndUploadTemplateProps> = (props) => {
  const {
    className,
    fileName,
    supportedFileTypes,
    isDownoadingTemplate,
    fields = [],
    showFields = true,
    showColumnsTemplate = true,
    downloadTemplate,
    uploadRequest,
  } = props;

  const { popError } = useContext(PopNotificationsContext);

  const hasFields = fields?.length > 0;

  const classes = classNames('get-and-upload-template', className);

  const { t } = useTranslation();

  const handleUpload: DropzoneUpload = useCallback(
    async (file, fileName, config, nameToUpload) => {
      if (file.size > SINGLE_FILE_MAX_SIZE_DIRECT_UPLOAD) {
        popError(t('fileTooLarge', { size: SINGLE_FILE_MAX_SIZE_MB }));
        return;
      }

      return await uploadRequest(file, fileName, config, nameToUpload);
    },
    [popError, t, uploadRequest],
  );

  return (
    <div className={classes}>
      {showFields && hasFields && (
        <ol>
          {fields.map(({ field, description }) => (
            <li key={field}>{t(description)}</li>
          ))}
        </ol>
      )}

      {showColumnsTemplate && hasFields && (
        <div className="fields">
          {fields.map(({ field }) => (
            <span key={field}>{t(field)}</span>
          ))}
        </div>
      )}

      <div className="file-actions">
        <Dropzone
          accept={supportedFileTypes}
          files={[]}
          maxFiles={1}
          fileName={fileName}
          deleteRequest={null}
          uploadRequest={handleUpload}
        />
        <Button
          variant="link"
          icon={DownloadIcon}
          disabled={isDownoadingTemplate}
          onClick={downloadTemplate}
        >
          {t('downloadTemplate')}
        </Button>
      </div>
    </div>
  );
};

export default GetAndUploadTemplate;
